import request from '@/util/request'

// Constants for pagination and API defaults
const DEFAULT_PAGINATION = {
  page: 1,
  itemsPerPage: 50
}

const DEFAULT_QUERY = {}

const ERROR_MESSAGES = {
  FETCH_CONTRACTS: 'Error fetching refinery contracts:',
  CREATE_CONTRACT: 'Error creating refinery contract:',
  DELETE_CONTRACT: 'Error deleting refinery contract:',
  FETCH_CONTRACT: 'Error fetching refinery contract:',
  UPDATE_CONTRACT: 'Error updating refinery contract:',
  FORK_CONTRACT: 'Error forking refinery contract:',
  FETCH_TECHNICIANS: 'Error fetching melt technicians:',
  CREATE_TECHNICIAN: 'Error creating melt technician:',
  FETCH_MELT_TECH_ELIGIBLE_USERS: 'Error fetching eligible melt technician users:',
  FETCH_FURNACES: 'Error fetching furnaces:',
  CREATE_FURNACE: 'Error creating furnace:',
  FETCH_VERSION_HISTORY: 'Error fetching contract version history:'
}

// Helper functions to eliminate duplication and improve readability
const refreshContractsList = async (dispatch) => {
  await dispatch('fetchRefineryContracts', {
    options: DEFAULT_PAGINATION,
    query: DEFAULT_QUERY
  })
}

const refreshAdminContractsList = async (dispatch) => {
  await dispatch('fetchRefineryContractsAdmin', {
    options: DEFAULT_PAGINATION,
    query: DEFAULT_QUERY
  })
}

const normalizeResponse = (response) => {
  return response?.result || response
}

const state = {
  contracts: {
    data: {
      count: 0,
      rows: []
    }
  },
  selectedContract: {},
  contractVersionHistory: {
    data: [],
    loading: false,
    error: null
  },
  meltTechnicians: {
    data: [],
    loading: false,
    error: null
  },
  furnaces: {
    data: [],
    loading: false,
    error: null
  },
  meltTechEligibleUsers: {
    data: [],
    loading: false,
    error: null
  }
}

const getters = {
  getRefineryContracts: (state) => {
    return state.contracts
  },
  canManageContracts: (state) => {
    if (Object.prototype.hasOwnProperty.call(state.contracts?.data?.can || {}, 'manageContracts')) {
      return Boolean(state.contracts.data.can.manageContracts)
    }
    return Boolean(state.selectedContract?.can?.manageContracts)
  },
  getSelectedRefineryContract: (state) => state.selectedContract,
  getContractVersionHistory: (state) => state.contractVersionHistory.data,
  getContractVersionHistoryLoading: (state) => state.contractVersionHistory.loading,
  getContractVersionHistoryError: (state) => state.contractVersionHistory.error,
  getMeltTechnicians: (state) => state.meltTechnicians.data,
  getMeltTechniciansLoading: (state) => state.meltTechnicians.loading,
  getMeltTechniciansError: (state) => state.meltTechnicians.error,
  getFurnaces: (state) => state.furnaces.data,
  getFurnacesLoading: (state) => state.furnaces.loading,
  getFurnacesError: (state) => state.furnaces.error,
  getMeltTechEligibleUsers: (state) => state.meltTechEligibleUsers.data,
  getMeltTechEligibleUsersLoading: (state) => state.meltTechEligibleUsers.loading,
  getMeltTechEligibleUsersError: (state) => state.meltTechEligibleUsers.error
}

const actions = {
  async fetchRefineryContracts({ commit, getters }, { options, query }) {
    try {
      const selectedCompany = getters.getSelectedCompany
      const companyId = selectedCompany && selectedCompany.id

      if (!companyId) {
        throw new Error('No company selected for fetchRefineryContracts')
      }

      const { data } = await request.get(`/refinery-contracts/${companyId}`,
        {
          params: {
            limit: options.itemsPerPage,
            offset: (options.page - 1) * options.itemsPerPage,
            sortBy: options.sortBy,
            sortDesc: options.sortDesc,
            query: query ? JSON.stringify(query) : undefined
        }
      })
      commit('SET_REFINERY_CONTRACTS', data)
    } catch (error) {
      console.error(ERROR_MESSAGES.FETCH_CONTRACTS, error)
      throw error
    }
  },
  async fetchRefineryContractsAdmin({ commit }, { options, query }) {
    try {
      const { data } = await request.get('/refinery-contracts/admin', {
        params: {
          limit: options.itemsPerPage,
          offset: (options.page - 1) * options.itemsPerPage,
          sortBy: options.sortBy,
          sortDesc: options.sortDesc,
          query: query ? JSON.stringify(query) : undefined
        }
      })
      commit('SET_REFINERY_CONTRACTS', data)
    } catch (error) {
      console.error('Error fetching admin refinery contracts:', error)
      throw error
    }
  },
  async fetchRefineryContractsByCompanyId({ commit, getters }, { options, query }) {
    try {
      const selectedCompany = getters.getSelectedCompany
      const companyId = selectedCompany && selectedCompany.id
      
      if (!companyId) {
        throw new Error('No company selected for fetchRefineryContractsByCompanyId')
      }
      
      const { data } = await request({
        url: `/refinery-contracts/${companyId}`,
        method: 'get',
        params: {
          limit: options.itemsPerPage,
          offset: (options.page - 1) * options.itemsPerPage,
          sortBy: options.sortBy,
          sortDesc: options.sortDesc,
          query: query ? JSON.stringify(query) : undefined
        }
      })
      commit('SET_REFINERY_CONTRACTS', data)
    } catch (error) {
      console.error('Error fetching refinery contracts by company ID:', error)
      throw error
    }
  },
  async createRefineryContract({ dispatch }, contractData) {
    try {
      await request.post('/refinery-contracts/admin', contractData)
      // Refresh the contracts list after creating a new one
      await refreshAdminContractsList(dispatch)
    } catch (error) {
      console.error(ERROR_MESSAGES.CREATE_CONTRACT, error)
      throw error
    }
  },
  async deleteRefineryContract({ dispatch }, id) {
    try {
      await request.delete(`/refinery-contracts/admin/${id}`)
      // Refresh the contracts list after deleting one
      await refreshAdminContractsList(dispatch)
    } catch (error) {
      console.error(ERROR_MESSAGES.DELETE_CONTRACT, error)
      throw error
    }
  },
  async fetchRefineryContractById({ commit, getters }, { id, isAdmin }) {
    try {
      // Determine if user is admin - use parameter if provided, otherwise check user profile
      const userIsAdmin = isAdmin !== undefined ? isAdmin : getters.getUserProfile?.isAdmin === true;

      let url;
      if (userIsAdmin) {
        // Admin route - doesn't require companyId
        url = `/refinery-contracts/admin/${id}`;
      } else {
        // Non-admin route - requires companyId
        const selectedCompany = getters.getSelectedCompany;
        const companyId = selectedCompany && selectedCompany.id;

        if (!companyId) {
          throw new Error('No company selected for fetchRefineryContractById');
        }

        url = `/refinery-contracts/${companyId}/${id}`;
      }

      const data = await request.get(url)
      commit('SET_SELECTED_REFINERY_CONTRACT', data)
      return data
    } catch (error) {
      console.error(ERROR_MESSAGES.FETCH_CONTRACT, error)
      throw error
    }
  },
  async updateRefineryContract({ commit }, { id, contractData }) {
    try {
      // Blank fields mean "no change", not "set to null" — strip nullish/NaN so the API preserves existing values.
      // TODO: gate cash-advance-interest fields on CASH_ADVANCE_INTEREST_ENABLED in ContractForm so they aren't sent at all when the flag is off.
      const sanitizedData = Object.fromEntries(
        Object.entries(contractData).filter(([, value]) => {
          if (value === null || value === undefined) return false
          if (typeof value === 'number' && Number.isNaN(value)) return false
          return true
        })
      )

      const eventResult = await request.patch(`/refinery-contracts/admin/${id}`, sanitizedData)
      const { result } = eventResult
      commit('SET_SELECTED_REFINERY_CONTRACT', result)
      return result
    } catch (error) {
      console.error(ERROR_MESSAGES.UPDATE_CONTRACT, error)
      throw error
    }
  },
  async forkRefineryContract({ dispatch }, { parentContractId, companyId, title, expirationDate }) {
    try {
      const data = await request.post('/refinery-contracts/admin/fork', {
        parentContractId,
        companyId,
        title,
        expirationDate
      })
      // Refresh the contracts list after forking
      await refreshAdminContractsList(dispatch)
      return data
  } catch (error) {
    console.error(ERROR_MESSAGES.FORK_CONTRACT, error)
    throw error
    }
  },
  async fetchContractVersionHistory({ commit, getters }, { contractId, isAdmin }) {
    commit('SET_CONTRACT_VERSION_HISTORY_LOADING', true)
    commit('SET_CONTRACT_VERSION_HISTORY_ERROR', null)
    
    try {
      // Determine if user is admin - use parameter if provided, otherwise check user profile
      const userIsAdmin = isAdmin !== undefined ? isAdmin : getters.getUserProfile?.isAdmin === true
      
      let url
      if (userIsAdmin) {
        // Admin route - doesn't require companyId
        url = `/refinery-contracts/admin/${contractId}/versions`
      } else {
        // Non-admin route - requires companyId
        const selectedCompany = getters.getSelectedCompany
        const companyId = selectedCompany && selectedCompany.id
        
        if (!companyId) {
          throw new Error('No company selected for fetchContractVersionHistory')
        }
        
        url = `/refinery-contracts/${companyId}/${contractId}/versions`
      }
      
      const response = await request.get(url)
      const versions = Array.isArray(response) ? response : (response?.data ?? [])
      commit('SET_CONTRACT_VERSION_HISTORY', versions)
      return response
    } catch (error) {
      console.error(ERROR_MESSAGES.FETCH_VERSION_HISTORY, error)
      commit('SET_CONTRACT_VERSION_HISTORY_ERROR', error.message || 'Failed to fetch version history')
      commit('SET_CONTRACT_VERSION_HISTORY', [])
      throw error
    } finally {
      commit('SET_CONTRACT_VERSION_HISTORY_LOADING', false)
    }
  },
  async fetchMeltTechnicians({ commit }) {
    try {
      commit('SET_MELT_TECHNICIANS_LOADING', true)
      commit('SET_MELT_TECHNICIANS_ERROR', null)
      
      const data = await request.get('/refinery/melt-technicians')
      
      commit('SET_MELT_TECHNICIANS', data)
      return data
    } catch (error) {
      console.error(ERROR_MESSAGES.FETCH_TECHNICIANS, error)
      commit('SET_MELT_TECHNICIANS_ERROR', error.message || 'Failed to fetch melt technicians')
      throw error
    } finally {
      commit('SET_MELT_TECHNICIANS_LOADING', false)
    }
  },
  async createMeltTechnician({ commit, dispatch }, { userId }) {
    try {
      commit('SET_MELT_TECHNICIANS_LOADING', true)
      commit('SET_MELT_TECHNICIANS_ERROR', null)

      const { data } = await request.post('/refinery/melt-technicians', { userId })
      await dispatch('fetchMeltTechnicians')
      return data
    } catch (error) {
      console.error(ERROR_MESSAGES.CREATE_TECHNICIAN, error)
      commit('SET_MELT_TECHNICIANS_ERROR', error.message || 'Failed to create melt technician')
      throw error
    } finally {
      commit('SET_MELT_TECHNICIANS_LOADING', false)
    }
  },
  async fetchMeltTechEligibleUsers({ commit }) {
    try {
      commit('SET_MELT_TECH_ELIGIBLE_USERS_LOADING', true)
      commit('SET_MELT_TECH_ELIGIBLE_USERS_ERROR', null)

      const data = await request.get('/refinery/melt-technicians/eligible-users')

      commit('SET_MELT_TECH_ELIGIBLE_USERS', data)
      return data
    } catch (error) {
      commit('SET_MELT_TECH_ELIGIBLE_USERS_ERROR', error.message || 'Failed to fetch eligible users for melt technicians')
      throw error
    } finally {
      commit('SET_MELT_TECH_ELIGIBLE_USERS_LOADING', false)
    }
  },
  async fetchFurnaces({ commit }) {
    try {
      commit('SET_FURNACES_LOADING', true)
      commit('SET_FURNACES_ERROR', null)
      const data = await request.get('/refinery/furnaces')
      commit('SET_FURNACES', data)
      return data
    } catch (error) {
      console.error(ERROR_MESSAGES.FETCH_FURNACES, error)
      commit('SET_FURNACES_ERROR', error.message || 'Failed to fetch furnaces')
      throw error
    } finally {
      commit('SET_FURNACES_LOADING', false)
    }
  },
  async createFurnace({ commit, dispatch }, { name }) {
    try {
      commit('SET_FURNACES_LOADING', true)
      commit('SET_FURNACES_ERROR', null)
      const response = await request.post('/refinery/furnaces', { name })
      const furnace = normalizeResponse(response)
      await dispatch('fetchFurnaces')
      return furnace
    } catch (error) {
      console.error(ERROR_MESSAGES.CREATE_FURNACE, error)
      commit('SET_FURNACES_ERROR', error.message || 'Failed to create furnace')
      throw error
    } finally {
      commit('SET_FURNACES_LOADING', false)
    }
  }
}

const mutations = {
  SET_REFINERY_CONTRACTS(state, payload) {
    state.contracts.data = payload?.data ?? payload
  },
  SET_SELECTED_REFINERY_CONTRACT(state, contract) {
    state.selectedContract = contract
  },
  SET_CONTRACT_VERSION_HISTORY(state, versions) {
    state.contractVersionHistory.data = versions
  },
  SET_CONTRACT_VERSION_HISTORY_LOADING(state, loading) {
    state.contractVersionHistory.loading = loading
  },
  SET_CONTRACT_VERSION_HISTORY_ERROR(state, error) {
    state.contractVersionHistory.error = error
  },
  SET_MELT_TECHNICIANS(state, technicians) {
    state.meltTechnicians.data = technicians
  },
  SET_MELT_TECHNICIANS_LOADING(state, loading) {
    state.meltTechnicians.loading = loading
  },
  SET_MELT_TECHNICIANS_ERROR(state, error) {
    state.meltTechnicians.error = error
  },
  ADD_MELT_TECHNICIAN(state, technician) {
    state.meltTechnicians.data.push(technician)
  },
  SET_FURNACES(state, furnaces) {
    state.furnaces.data = furnaces
  },
  SET_FURNACES_LOADING(state, loading) {
    state.furnaces.loading = loading
  },
  SET_FURNACES_ERROR(state, error) {
    state.furnaces.error = error
  },
  SET_MELT_TECH_ELIGIBLE_USERS(state, users) {
    state.meltTechEligibleUsers.data = users
  },
  SET_MELT_TECH_ELIGIBLE_USERS_LOADING(state, loading) {
    state.meltTechEligibleUsers.loading = loading
  },
  SET_MELT_TECH_ELIGIBLE_USERS_ERROR(state, error) {
    state.meltTechEligibleUsers.error = error
  }
}

export default {
  state,
  getters,
  actions,
  mutations
} 